import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { supabase } from '../lib/supabase';
import { ShoppingCart, Plus, Search, X, Coins, Download, Tag, Copy, CheckCircle2, AlertCircle } from 'lucide-react';
import { Database } from '../types/database.types';
import ModalOverlay from '../components/ui/ModalOverlay';

type MarketplaceItem = Database['public']['Tables']['marketplace_items']['Row'];

const CATEGORIES = [
  { value: 'geral', label: 'Geral' },
  { value: 'sala', label: 'Sala' },
  { value: 'quarto', label: 'Quarto' },
  { value: 'cozinha', label: 'Cozinha' },
  { value: 'escritorio', label: 'Escritório' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'comercial', label: 'Comercial' },
];

export default function Marketplace() {
  const { user } = useAuth();
  const { org, role, refreshOrg } = useOrg();
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showPublish, setShowPublish] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MarketplaceItem | null>(null);
  const [feedback, setFeedback] = useState<{type:'success'|'error';message:string}|null>(null);
  const [publishForm, setPublishForm] = useState({ title: '', description: '', type: 'prompt' as string, content: '', category: 'geral', price_credits: 5, tags: '' });

  const isAdminOrFinOps = role === 'admin' || role === 'fin_ops';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('marketplace_items').select('*').eq('is_active', true).order('created_at', { ascending: false });
    if (filterCat !== 'all') query = query.eq('category', filterCat);
    if (filterType !== 'all') query = query.eq('type', filterType);
    const { data } = await query;
    let filtered = data || [];
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(i => i.title.toLowerCase().includes(s) || (i.description || '').toLowerCase().includes(s));
    }
    setItems(filtered);
    setLoading(false);
  }, [filterCat, filterType, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSearch = () => { fetchItems(); };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org || !user) return;
    try {
      const tags = publishForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      const { error } = await supabase.from('marketplace_items').insert({
        org_id: org.id,
        author_id: user.id,
        title: publishForm.title,
        description: publishForm.description,
        type: publishForm.type,
        content: publishForm.content,
        category: publishForm.category,
        price_credits: publishForm.price_credits,
        tags,
      });
      if (error) throw error;
      setFeedback({ type: 'success', message: 'Item publicado no Marketplace!' });
      setShowPublish(false);
      setPublishForm({ title: '', description: '', type: 'prompt', content: '', category: 'geral', price_credits: 5, tags: '' });
      fetchItems();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const handleBuy = async (item: MarketplaceItem) => {
    if (!org || !user) return;
    if (!isAdminOrFinOps) {
      // Employee: create request
      try {
        await supabase.from('requests').insert({
          org_id: org.id,
          requester_id: user.id,
          type: 'marketplace_purchase',
          title: `Compra: ${item.title}`,
          description: `Solicito a compra do item "${item.title}" por ${item.price_credits} créditos.`,
          metadata: { marketplace_item_id: item.id, item_title: item.title, item_price: item.price_credits },
        });
        setFeedback({ type: 'success', message: 'Solicitação de compra enviada ao administrador' });
      } catch (err: any) {
        setFeedback({ type: 'error', message: err.message });
      }
      return;
    }

    if (!window.confirm(`Comprar "${item.title}" por ${item.price_credits} créditos?`)) return;

    if ((org.available_credits ?? 0) < item.price_credits) {
      setFeedback({ type: 'error', message: 'Créditos insuficientes' });
      return;
    }

    try {
      const { error } = await supabase.rpc('purchase_marketplace_item', { p_item_id: item.id, p_org_id: org.id });
      if (error) throw error;

      setFeedback({ type: 'success', message: 'Compra realizada com sucesso!' });
      refreshOrg();
      fetchItems();
      setSelectedItem(null);
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Marketplace</h1>
          <p className="subtitle">Compre e venda prompts e imagens da comunidade</p>
        </div>
        <button className="btn-primary" style={{ width: 'auto', padding: '0.7rem 1.5rem' }} onClick={() => setShowPublish(true)}>
          <Plus size={18} /> Publicar Item
        </button>
      </div>

      {feedback && (
        <div className={`alert-banner ${feedback.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {feedback.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{feedback.message}</span>
          <button className="alert-dismiss" onClick={() => setFeedback(null)}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="marketplace-filters">
        <div className="search-bar">
          <Search size={18} />
          <input type="text" placeholder="Buscar prompts e imagens..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
        </div>
        <select className="form-select form-select-sm" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="all">Todas Categorias</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select className="form-select form-select-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">Todos Tipos</option>
          <option value="prompt">Prompts</option>
          <option value="image">Imagens</option>
          <option value="scene_tag">Tags de Cena</option>
        </select>
      </div>

      {/* Publish Modal */}
      {showPublish && (
        <ModalOverlay onClose={() => setShowPublish(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Publicar no Marketplace</h2>
              <button className="modal-close" onClick={() => setShowPublish(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handlePublish}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Título</label>
                  <input type="text" required value={publishForm.title} onChange={e => setPublishForm({ ...publishForm, title: e.target.value })} placeholder="Prompt Sala Moderna Minimalista" />
                </div>
                <div className="form-group" style={{ width: 150 }}>
                  <label>Tipo</label>
                  <select className="form-select" value={publishForm.type} onChange={e => setPublishForm({ ...publishForm, type: e.target.value })}>
                    <option value="prompt">Prompt</option>
                    <option value="image">Imagem</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea className="form-textarea" value={publishForm.description} onChange={e => setPublishForm({ ...publishForm, description: e.target.value })} placeholder="Descreva o que este prompt/imagem gera..." rows={2} />
              </div>
              <div className="form-group">
                <label>{publishForm.type === 'prompt' ? 'Prompt Completo' : 'URL da Imagem'}</label>
                <textarea className="form-textarea" required value={publishForm.content} onChange={e => setPublishForm({ ...publishForm, content: e.target.value })} placeholder={publishForm.type === 'prompt' ? 'Cole o prompt completo aqui...' : 'https://...'} rows={4} />
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Categoria</label>
                  <select className="form-select" value={publishForm.category} onChange={e => setPublishForm({ ...publishForm, category: e.target.value })}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ width: 150 }}>
                  <label>Preço (créditos)</label>
                  <input type="number" min={1} max={100} value={publishForm.price_credits} onChange={e => setPublishForm({ ...publishForm, price_credits: parseInt(e.target.value) || 1 })} />
                </div>
              </div>
              <div className="form-group">
                <label>Tags (separadas por vírgula)</label>
                <input type="text" value={publishForm.tags} onChange={e => setPublishForm({ ...publishForm, tags: e.target.value })} placeholder="moderno, minimalista, luxo" />
              </div>
              <button type="submit" className="btn-primary">Publicar</button>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <ModalOverlay onClose={() => setSelectedItem(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedItem.title}</h2>
              <button className="modal-close" onClick={() => setSelectedItem(null)}><X size={20} /></button>
            </div>
            <div className="marketplace-detail">
              <p className="text-muted">{selectedItem.description}</p>
              <div className="detail-meta">
                <span className={`badge badge-${selectedItem.type === 'prompt' ? 'info' : selectedItem.type === 'scene_tag' ? 'warning' : 'success'}`}>
                {selectedItem.type === 'prompt' ? '📝 Prompt' : selectedItem.type === 'scene_tag' ? '🏷️ Tag de Cena' : '🖼 Imagem'}
              </span>
                <span className="badge badge-role-admin">{selectedItem.category}</span>
                <span className="text-muted"><Download size={14} /> {selectedItem.downloads}</span>
              </div>
              <div className="detail-content">
                <h3>Conteúdo</h3>
                <pre className="content-preview">{selectedItem.content}</pre>
              </div>
              {selectedItem.tags && selectedItem.tags.length > 0 && (
                <div className="detail-tags">
                  {selectedItem.tags.map((t, i) => <span key={i} className="tag"><Tag size={12} /> {t}</span>)}
                </div>
              )}
              <div className="detail-actions">
                <div className="detail-price"><Coins size={20} /> {selectedItem.price_credits} créditos</div>
                {selectedItem.author_id !== user?.id && (
                  <button className="btn-primary" style={{ width: 'auto' }} onClick={() => handleBuy(selectedItem)}>
                    {isAdminOrFinOps ? <><ShoppingCart size={16} /> Comprar</> : <><ShoppingCart size={16} /> Solicitar Compra</>}
                  </button>
                )}
                {selectedItem.type === 'prompt' && (
                  <button className="btn-secondary" onClick={() => { navigator.clipboard.writeText(selectedItem.content); setFeedback({ type: 'success', message: 'Prompt copiado!' }); }}>
                    <Copy size={16} /> Copiar
                  </button>
                )}
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : items.length === 0 ? (
        <div className="glass-card wide-card">
          <div className="empty-state">
            <ShoppingCart size={48} />
            <p>Nenhum item no marketplace. Seja o primeiro a publicar!</p>
          </div>
        </div>
      ) : (
        <div className="marketplace-grid">
          {items.map(item => (
            <div key={item.id} className="marketplace-card" onClick={() => setSelectedItem(item)}>
              <div className="mp-card-thumb">
                {item.thumbnail_url ? <img src={item.thumbnail_url} alt={item.title} /> : (
              <div className="mp-card-placeholder">{item.type === 'prompt' ? '📝' : item.type === 'scene_tag' ? '🏷️' : '🖼'}</div>
                )}
                <span className={`mp-card-type badge-${item.type === 'prompt' ? 'info' : item.type === 'scene_tag' ? 'warning' : 'success'}`}>
                  {item.type === 'prompt' ? 'Prompt' : item.type === 'scene_tag' ? 'Tag' : 'Imagem'}
                </span>
              </div>
              <div className="mp-card-body">
                <h3>{item.title}</h3>
                <p className="text-muted text-sm">{(item.description || '').substring(0, 60)}{(item.description || '').length > 60 ? '...' : ''}</p>
                <div className="mp-card-footer">
                  <span className="mp-price"><Coins size={14} /> {item.price_credits}</span>
                  <span className="text-muted text-sm"><Download size={12} /> {item.downloads}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
